import {axiosClientAPI} from '@/lib/axios';
import {apiRoutes} from '@/lib/routes/api';
import type {IDashboardData, IDashboardSummary, Notification} from '@/types/dashboard';
import type {IScenarioDetail} from '@/types/scenarios';
import type {TReportDetailItem, TReportList} from '@/types/reports';

export interface InjectType {
    id: number;
    code: string;
    name: string;
    has_options: boolean;
    detail_alert_media_type: string;
}

export interface InjectRole {
    id: number;
    code: string;
    name: string;
    order: number;
}

export interface InjectOption {
    id?: number;
    text: string;
    order: number;
    is_correct: boolean;
}

/** Exercise run (TTX) for an active scenario */
export interface ExerciseRunResponse {
    id: number;
    /** Stable run uuid (optional; list/detail active id uses numeric ``id`` / PK). */
    uuid?: string;
    name: string;
    /** Display name of the user who created the run (when set on the model). */
    started_by?: string;
    scenario_id: string;
    /** Relational ExerciseModel UUID when provided at create (immutable archives). */
    exercise_id?: string | null;
    /** Present on v2 active-scenario exercise-run responses; omitted on pure Django serializer reads. */
    active_scenario_id?: string;
    start_at: string;
    end_at: string | null;
    /** Django ExerciseRunSerializer — launch gate for participants */
    is_launched?: boolean;
    launched_at?: string | null;
    scheduled_for?: string | null;
    status_display?: string;
    current_status?: string;
    status: string;
    /** Set when ``status`` is ``failed`` (OpenStack / provisioning error). */
    failure_detail?: string | null;
    participants: ExerciseRunParticipant[];
    /**
     * Merged scenario detail returned by `GET /api/scenarios/active/<run_id>`.
     * Includes `infrastructure` summary under `scenario.infrastructure`.
     */
    scenario?: IScenarioDetail;
}

/** Django ``ExerciseRunParticipantModel.access_mode`` */
export type ParticipantAccessMode = 'vm_only' | 'ttx_only' | 'combined';

export interface ExerciseRunParticipant {
    id: number;
    user_id: number;
    user_email: string;
    user_full_name: string;
    role_id: number;
    role_name: string;
    user?: { id: number; email: string; full_name: string };
    team?: { id: number; name: string; code: string } | null;
    access_mode?: ParticipantAccessMode;
    has_vm_access?: boolean;
    has_ttx_access?: boolean;
    assigned_compute_instance_id?: string | null;
    assigned_compute_instance_name?: string | null;
    started_at?: string | null;
    end_at?: string | null;
    injects: Array<{
        id: number;
        inject_id: number;
        title: string;
        inject_type?: string;
        inject_type_name?: string;
        points: number;
        points_type?: 'manual' | 'decay' | 'default';
        sent_at: string;
        response_text?: string;
        selected_option_ids?: number[];
        selected_options?: Array<{ id: number; text: string; order: number }>;
        options?: Array<{ id: number; text: string; order: number; is_correct?: boolean }>;
        responded_at?: string | null;
        obtained_score?: number | null;
        bonus_points?: number | null;
        evaluated_at?: string | null;
        evaluated_by?: number | null;
        evaluator_name?: string;
        remarks?: string;
        is_evaluated?: boolean;
        status: 'not_answered' | 'answered_pending' | 'scored';
        phases?: Array<{ id: number; name: string; order?: number; slug?: string }>;
    }>;
    flags: Array<{
        flag_id: string | number;
        map_id?: number;
        order?: number;
        is_required?: boolean;
        question?: string;
        score?: number;
        obtained_score?: number;
        team?: number | null;
        team_name?: string;
        answers?: unknown[];
    }>;
    walkthrough_files?: Array<{
        id: number;
        title: string;
        file: string;
        file_url: string;
        team?: number;
        team_name?: string;
        created_at?: string;
        updated_at?: string;
    }>;
}

/** User search result for adding as run participant */
export interface ExerciseRunUserSearchItem {
    id: number;
    email: string;
    full_name: string;
    expected_action: string;
}

/** GET .../participant-role-options — teams eligible for the chosen access mode */
export interface ParticipantRoleOptionsResponse {
    access_mode_locked: boolean;
    forced_access_mode: ParticipantAccessMode | null;
    effective_access_mode: ParticipantAccessMode;
    roles: InjectRole[];
}

/** GET /api/exercises/<exercise_id>/archives */
export interface ExerciseArchiveListItem {
    uuid: string;
    name: string;
    scenario_id: string;
    active_scenario_id: string;
    start_at: string;
    end_at: string | null;
    archived_at: string;
    report_url: string;
}

/** GET /api/exercises/archives (paginated) or per-exercise list (count + results only). */
export interface ExerciseArchiveListResponse {
    count: number;
    results: ExerciseArchiveListItem[];
    total_pages?: number;
    current_page?: number;
    next?: string | null;
    previous?: string | null;
}

export interface ExerciseArchiveBulkReportResponse {
    count: number;
    results: Array<{
        archive_uuid: string;
        report: ImmutableExerciseReport;
    }>;
    missing: string[];
}

/** GET /api/exercises/archives/<uuid>/report — immutable snapshot only */
export interface ImmutableExerciseArchivePayload {
    uuid: string;
    name: string;
    scenario_id: string;
    active_scenario_id: string;
    exercise_id: string | null;
    start_at: string;
    end_at: string | null;
    status?: string;
    archived_at: string;
    definition_snapshot: Record<string, unknown>;
}

export interface ImmutableExerciseReportParticipant {
    id: number;
    user_id: number;
    team: { id: number; name: string };
    full_name: string;
    email: string;
    access_mode: string;
    score?: {
        ttx?: {
            out_of: number;
            per?: number;
            obtained: { obtained_score: number; bonus_points: number };
            phase: Array<{
                name: string;
                out_of: number;
                obtained_score: number;
                bonus_points: number;
            }>;
        };
        scenarios?: {
            out_of: number;
            per?: number;
            obtained: { obtained_score: number; bonus_points: number };
            phase: Array<{
                name: string;
                out_of: number;
                obtained_score: number;
                bonus_points: number;
            }>;
        };
    };
}

export interface ImmutableExerciseReportDelivery {
    id: number;
    user_id: number;
    role_id: number;
    participant_assignment_id: number | null;
    inject_id: number;
    inject_title: string;
    inject_points: number;
    inject_points_type: string;
    inject_phases_snapshot: Array<{ id: number; name: string }>;
    sent_at: string | null;
    response_text: string;
    responded_at: string | null;
    attempt_count?: number;
    is_correct?: boolean;
    hint_used?: boolean;
    hint_used_at_list?: string[];
    obtained_score: number;
    bonus_points: number;
    evaluated_by_id: number | null;
    evaluated_at: string | null;
    evaluator_name: string;
    remarks: string;
    selected_option_ids: number[];
}

export interface ImmutableExerciseReport {
    id: number;
    uuid: string;
    name: string;
    status?: string;
    start_at?: string;
    end_at?: string | null;
    scenario: Record<string, unknown>;
    participants: ImmutableExerciseReportParticipant[];
    team_scores?: Array<{
        team: { id: number; name: string };
        score: {
            ttx: {
                out_of: number;
                per?: number;
                obtained: { obtained_score: number; bonus_points: number };
                phase: Array<{
                    name: string;
                    out_of: number;
                    obtained_score: number;
                    bonus_points: number;
                }>;
            };
            scenarios: {
                out_of: number;
                per?: number;
                obtained: { obtained_score: number; bonus_points: number };
                phase: Array<{
                    name: string;
                    out_of: number;
                    obtained_score: number;
                    bonus_points: number;
                }>;
            };
            grand_total?: {
                out_of: number;
                per?: number;
                obtained: { obtained_score: number; bonus_points: number };
            };
        };
    }>;
    summary?: {
        participants_count: number;
        teams_count: number;
        ttx: {
            out_of: number;
            per?: number;
            obtained: { obtained_score: number; bonus_points: number };
        };
        scenarios: {
            out_of: number;
            per?: number;
            obtained: { obtained_score: number; bonus_points: number };
        };
        grand_total: {
            out_of: number;
            per?: number;
            obtained: { obtained_score: number; bonus_points: number };
        };
    };
    inject_deliveries: ImmutableExerciseReportDelivery[];
    flags: Array<Record<string, unknown>>;
    feedback?: Array<{
        id: number;
        participant_id: number | null;
        participant_user_id: number | null;
        submitted_by_id: number | null;
        submitted_by_name?: string;
        payload: Record<string, unknown>;
        created_at?: string | null;
        updated_at?: string | null;
    }>;
}

export interface ScenarioInject {
    id: number;
    /** Present when API includes it; used to prefill Exercises mapping. */
    scenario_id?: string;
    // All scenario IDs (TTX configs) this inject is linked to.
    // The current page's scenario_id will always be included when listing by scenario.
    ttx_config_ids?: string[];
    title: string;
    description: string;
    order: number;
    severity: string;
    scheduled_offset_minutes: number;
    points: number;
    /** How points are applied for this inject (manual scoring, decay over time, or default behavior). */
    points_type?: 'manual' | 'decay' | 'default';
    max_answer_attempts: number;
    inject_type: InjectType;
    options: InjectOption[];
    correct_answer: string;
    detail_alert_image_url: string;
    detail_alert_text: string;
    phase_ids: number[];
    team_ids: number[];
    /** Teams this inject targets (empty when apply_to_all_team). */
    teams?: Array<{ id: number; name: string; code: string }>;
    apply_to_all_phases: boolean;
    apply_to_all_team: boolean;
    created_at: string;
    updated_at: string;
}

export interface NewPhaseInline {
    name: string;
    slug?: string;
    expected_action?: string;
    order?: number;
}

export interface NewTeamInline {
    name: string;
    code?: string;
}

export interface ScenarioInjectPayload {
    title: string;
    description: string;
    order?: number;
    severity?: string;
    scheduled_offset_minutes?: number;
    /** Total points available for this inject (required). */
    points: number;
    /** How points are applied for this inject. If omitted, backend defaults to "default". */
    points_type?: 'manual' | 'decay' | 'default';
    max_answer_attempts?: number;
    inject_type: number;
    options?: { text: string; order: number; is_correct: boolean }[];
    correct_answer?: string;
    detail_alert_image_url?: string;
    detail_alert_text?: string;
    phase_ids?: number[];
    team_ids?: number[];
    new_phases?: NewPhaseInline[];
    new_teams?: NewTeamInline[];
    apply_to_all_phases?: boolean;
    apply_to_all_team?: boolean;
    /** Optional: override all scenario mappings (TTX configs) for this inject */
    scenario_ids?: string[];
}

export interface Phase {
    id: number;
    name: string;
    order: number;
    slug: string;
    description: string;
    thumbnail: string | null;
    thumbnail_url: string | null;
    company: number | null;
    company_name: string | null;
    created_at: string;
    updated_at: string;
}

export interface Company {
    id: number;
    name: string;
    code: string;
    is_active: boolean;
}

export interface PhasePayload {
    name: string;
    order?: number;
    slug: string;
    description?: string;
    thumbnail?: File | null;
    company?: number | null;
}

/** Exercise phase: links a scenario (exercise_id) to a phase with order, duration, lock */
export interface ExercisePhase {
    id: number;
    uuid: string;
    exercise_id: string;
    phase: number;
    phase_name: string;
    phase_slug: string;
    order: number;
    duration_mins: number;
    created_at?: string;
    updated_at?: string;
}

export interface ExercisePhasePayload {
    phase: number;
    order?: number;
    duration_mins?: number;
    exercise_id?: string;
}

export interface ExerciseWalkthrough {
    id: number;
    exercise: string;
    team: number;
    exercise_team: number | null;
    title: string;
    file: string;
    file_url: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface ExerciseTeamMapping {
    id: number;
    exercise: string;
    team: number;
    team_name: string;
    team_code: string;
    vm_required?: boolean;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface ExerciseTeamMappingPayload {
    team: number;
    vm_required?: boolean;
    is_active?: boolean;
}

export interface ExerciseFlagMapping {
    id: number;
    exercise: string;
    exercise_team: number | null;
    flag: string;
    order: number;
    is_required: boolean;

    flag_question: string;
    flag_answer: string;
    flag_hint: string;
    flag_hint_penalty: number | null;
    flag_score: number;
    flag_score_type: string;
    flag_placeholder?: string;

    flag_team: number;
    flag_team_name?: string;
    flag_phase: number | null;
    flag_phase_name?: string;

    created_at?: string;
    updated_at?: string;
}

export interface ExerciseFlagPayload {
    exercise_team: number;
    is_required?: boolean;
    order?: number;

    // If provided, backend will link to an existing FlagModel.
    flag?: string;

    // If `flag` is not provided, backend creates/updates a FlagModel using these fields.
    question?: string;
    answer?: string;
    hint?: string;
    placeholder?: string;
    hint_penalty?: number | null;
    score?: number;
    phase?: number | null; // PhaseModel id

    // Optional; defaults to model's standard choice.
    score_type?: string;
}

export interface FlagModelDto {
    id: string;
    team: number;
    phase: number | null;
    question: string;
    answer: string;
    hint: string;
    hint_penalty: number | null;
    score: number;
    score_type: string;
    created_at?: string;
    updated_at?: string;
}

export type FlagModelPayload = {
    team: number;
    phase?: number | null;
    question?: string;
    answer?: string;
    hint?: string;
    placeholder?: string;
    hint_penalty?: number | null;
    score?: number;
    score_type?: string;
};

export interface ExerciseWalkthroughPayload {
    title?: string;
    exercise_team?: number;
    file?: File;
    is_active?: boolean;
}

export interface V2Paginated<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
    current_page?: number;
    total_pages?: number;
}

function normalizePaginated<T>(data: unknown): V2Paginated<T> {
    if (Array.isArray(data)) {
        return {
            count: data.length,
            next: null,
            previous: null,
            results: data as T[],
        };
    }

    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;

        // DRF-style pagination
        if (Array.isArray(obj.results)) {
            return {
                count: typeof obj.count === 'number' ? obj.count : obj.results.length,
                next: typeof obj.next === 'string' ? obj.next : null,
                previous: typeof obj.previous === 'string' ? obj.previous : null,
                results: obj.results as T[],
                current_page: typeof obj.current_page === 'number' ? obj.current_page : undefined,
                total_pages: typeof obj.total_pages === 'number' ? obj.total_pages : undefined,
            };
        }

        // Some endpoints nest arrays under different keys
        if (Array.isArray(obj.notifications)) {
            return {
                count: (obj.notifications as unknown[]).length,
                next: null,
                previous: null,
                results: obj.notifications as T[],
            };
        }
    }

    return {count: 0, next: null, previous: null, results: []};
}

async function v2Get<T>(
    url: string,
    params?: Record<string, string | number | boolean | undefined | null>,
): Promise<T> {
    const normalizedParams =
        params &&
        (Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        ) as Record<string, string | number | boolean>);

    const res = await axiosClientAPI.get<T>(url, {
        params: normalizedParams && Object.keys(normalizedParams).length ? normalizedParams : undefined,
    });
    return res.data;
}

async function v2Post<T>(
    url: string,
    data?: unknown,
    params?: Record<string, string | number | boolean | undefined | null>,
): Promise<T> {
    const normalizedParams =
        params &&
        (Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        ) as Record<string, string | number | boolean>);

    const res = await axiosClientAPI.post<T>(url, data ?? {}, {
        params: normalizedParams && Object.keys(normalizedParams).length ? normalizedParams : undefined,
    });
    return res.data;
}

async function v2Patch<T>(url: string, data?: unknown): Promise<T> {
    const res = await axiosClientAPI.patch<T>(url, data ?? {});
    return res.data;
}

export interface NotificationFilters {
    search?: string;
    type?: string;
    read?: boolean | null;
    ordering?: string;
    user?: string;
    page?: number;
    page_size?: number;
}

export interface ConsoleData {
    vnc_url?: string;
    vnc_host?: string;
    vnc_port?: number;
    token?: string;
    path?: string;
}

export const v2Api = {
    dashboard: {
        summary: {
            get: async () => v2Get<IDashboardSummary>(apiRoutes.dashboard.summary),
        },
        analytics: {
            get: async () => v2Get<IDashboardData>(apiRoutes.dashboard.analytics),
        },

        notifications: {
            list: async (filters?: NotificationFilters) => {
                const data = await v2Get<unknown>(apiRoutes.dashboard.notifications.list, {
                    q: filters?.search,
                    type: filters?.type && filters.type !== 'all' ? filters.type : undefined,
                    read:
                        filters?.read !== undefined && filters?.read !== null ? String(filters.read) : undefined,
                    ordering: filters?.ordering,
                    user: filters?.user,
                    page: filters?.page,
                    page_size: filters?.page_size,
                });
                return normalizePaginated<Notification>(data);
            },

            markRead: async (id: string) =>
                v2Post<{ id: string; read: boolean }>(apiRoutes.dashboard.notifications.markRead(id)),

            markUnread: async (id: string) =>
                v2Post<{ id: string; read: boolean }>(apiRoutes.dashboard.notifications.markUnread(id)),

            markAllRead: async (userId?: string) =>
                v2Post<{ updated: number }>(apiRoutes.dashboard.notifications.markAllRead, undefined, {
                    user: userId,
                }),

            delete: async (id: string) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.notifications.delete(id));
            },
        },

        users: {
            list: async <T = unknown>(params?: {
                q?: string;
                team?: string;
                status?: string;
                page?: string | number;
                sort?: string;
            }) => {
                const data = await v2Get<unknown>(apiRoutes.dashboard.users.list, params);
                return normalizePaginated<T>(data);
            },

            detail: async <T = unknown>(userId: string) =>
                v2Get<T>(apiRoutes.dashboard.users.detail(userId)),

            report: async <T = unknown>(userId: string) =>
                v2Get<T>(apiRoutes.dashboard.users.report(userId)),

            changePassword: async <T = unknown>(
                userId: string,
                payload: { email: string; password: string; confirm_password: string },
            ) => v2Post<T>(apiRoutes.dashboard.users.changePassword(userId), payload),

            update: async <T = unknown>(userId: string, payload: unknown) =>
                v2Patch<T>(apiRoutes.dashboard.users.update(userId), payload),
        },

        scenarios: {
            list: async <T = unknown>(params?: {
                q?: string;
                ttx_enabled?: boolean | string;
                is_approved?: boolean | string;
                page?: string | number;
            }) => {
                const data = await v2Get<unknown>(apiRoutes.dashboard.scenarios.list, params);
                return normalizePaginated<T>(data);
            },

            detail: async (scenarioId: string) =>
                v2Get<IScenarioDetail>(apiRoutes.dashboard.scenarios.detail(scenarioId)),

            setApproval: async (
                scenarioId: string,
                isApproved: boolean,
            ) =>
                v2Patch<{
                    exercise_id: string;
                    is_approved: boolean;
                    is_prepared: boolean;
                    can_approve: boolean;
                    prepare_validation_error: string | null;
                }>(apiRoutes.dashboard.scenarios.approval(scenarioId), {is_approved: isApproved}),

            ttx: {
                get: async (scenarioId: string) =>
                    v2Get<{ scenario_id: string; ttx_enabled: boolean; is_active: boolean }>(
                        apiRoutes.dashboard.scenarios.ttx(scenarioId),
                    ),
                set: async (scenarioId: string, ttxEnabled: boolean) =>
                    v2Patch<{ scenario_id: string; ttx_enabled: boolean; is_active: boolean }>(
                        apiRoutes.dashboard.scenarios.ttx(scenarioId),
                        {ttx_enabled: ttxEnabled},
                    ),
            },

            injects: {
                /** List injects for a scenario (exercise UUID). */
                list: async (scenarioId: string) =>
                    v2Get<ScenarioInject[]>(apiRoutes.dashboard.scenarios.injects(scenarioId)),
                /** All injects with optional filter/sort. Params: scenario_id, search, severity, inject_type, ordering */
                listAll: async (params?: {
                    scenario_id?: string;
                    search?: string;
                    severity?: string;
                    inject_type?: number;
                    ordering?: string;
                }) =>
                    v2Get<ScenarioInject[]>(apiRoutes.dashboard.injectsAll, params),
                /** Create inject. POST /api/injects/ with body { ...payload, scenario_id } */
                create: async (scenarioId: string, payload: ScenarioInjectPayload) =>
                    v2Post<ScenarioInject>(apiRoutes.dashboard.scenarios.injects(scenarioId), payload),
                /** Get one inject. GET /api/injects/<id>/ */
                get: async (_scenarioId: string, injectId: number) =>
                    v2Get<ScenarioInject>(
                        apiRoutes.dashboard.scenarios.injectDetail(_scenarioId, injectId),
                    ),
                /** Update inject. PATCH /api/injects/<id>/ */
                update: async (_scenarioId: string, injectId: number, payload: Partial<ScenarioInjectPayload>) =>
                    v2Patch<ScenarioInject>(
                        apiRoutes.dashboard.scenarios.injectDetail(_scenarioId, injectId),
                        payload,
                    ),
                /** Delete inject. DELETE /api/injects/<id>/ */
                delete: async (_scenarioId: string, injectId: number) => {
                    await axiosClientAPI.delete(
                        apiRoutes.dashboard.scenarios.injectDetail(_scenarioId, injectId),
                    );
                },
            },

            start: async <T = unknown>(scenarioId: string, payload: unknown) =>
                v2Post<T>(apiRoutes.dashboard.scenarios.start(scenarioId), payload),

            actives: {
                list: async <T = unknown>(params?: { page?: string | number }) => {
                    const data = await v2Get<unknown>(apiRoutes.dashboard.scenarios.actives.list, params);
                    return normalizePaginated<T>(data);
                },

                detail: async (activeScenarioId: string) =>
                    v2Get<any>(apiRoutes.dashboard.scenarios.actives.detail(activeScenarioId)),

                console: async (activeScenarioId: string, participantId?: string) => {
                    const raw = participantId?.trim();
                    const valid =
                        raw &&
                        raw !== 'undefined' &&
                        raw !== 'null' &&
                        raw !== 'NaN';
                    return v2Get<ConsoleData>(
                        apiRoutes.dashboard.scenarios.actives.console(activeScenarioId),
                        valid ? { participant: raw } : undefined,
                    );
                },

                end: async (activeScenarioId: string) =>
                    v2Post<Record<string, unknown>>(
                        apiRoutes.dashboard.scenarios.actives.end(activeScenarioId),
                    ),

                /** Exercise run (TTX): get or create for manage participants */
                run: {
                    get: async (activeScenarioId: string) =>
                        v2Get<ExerciseRunResponse>(
                            apiRoutes.dashboard.scenarios.actives.exerciseRun(activeScenarioId),
                        ),
                    create: async (
                        activeScenarioId: string,
                        payload: { name: string; scenario_id: string; exercise_id?: string },
                    ) =>
                        v2Post<ExerciseRunResponse>(
                            apiRoutes.dashboard.scenarios.actives.exerciseRun(activeScenarioId),
                            payload,
                        ),
                },

                /** Send an inject to all matching TTX participants for this active scenario. */
                sendInject: async (activeScenarioId: string, injectId: number) =>
                    v2Post<Record<string, unknown>>(
                        apiRoutes.dashboard.scenarios.actives.sendInject(activeScenarioId, injectId),
                    ),

                /** Evaluate a specific inject delivery (manual scoring). */
                markRankInjectDelivery: async (
                    activeScenarioId: string,
                    deliveryId: number,
                    payload: { obtained_score: number; bonus_points?: number; remarks?: string },
                ) =>
                    v2Post<Record<string, unknown>>(
                        apiRoutes.dashboard.scenarios.actives.markRankInjectDelivery(activeScenarioId, deliveryId),
                        payload,
                    ),

                /** @deprecated prefer markRankInjectDelivery */
                evaluateInjectDelivery: async (
                    activeScenarioId: string,
                    deliveryId: number,
                    payload: { obtained_score?: number; bonus_points?: number; remarks?: string },
                ) =>
                    v2Post<Record<string, unknown>>(
                        apiRoutes.dashboard.scenarios.actives.evaluateInjectDelivery(activeScenarioId, deliveryId),
                        payload,
                    ),

                /** Run participants: list, add, remove */
                participants: {
                    list: async (activeScenarioId: string) =>
                        v2Get<ExerciseRunParticipant[]>(
                            apiRoutes.dashboard.scenarios.actives.participants(activeScenarioId),
                        ),
                    add: async (
                        activeScenarioId: string,
                        payload: { user_id: number; role_id: number; access_mode?: ParticipantAccessMode },
                    ) =>
                        v2Post<ExerciseRunParticipant>(
                            apiRoutes.dashboard.scenarios.actives.participants(activeScenarioId),
                            payload,
                        ),
                    remove: async (activeScenarioId: string, userId: string) => {
                        await axiosClientAPI.delete(
                            apiRoutes.dashboard.scenarios.actives.participantRemove(
                                activeScenarioId,
                                userId,
                            ),
                        );
                    },

                    /** Search users to add (min 2 chars). Optionally exclude already assigned and filter by role. */
                    searchUsers: async (
                        activeScenarioId: string,
                        q: string,
                        excludeAssigned = true,
                        roleId?: string,
                    ) =>
                        v2Get<ExerciseRunUserSearchItem[]>(
                            apiRoutes.dashboard.scenarios.actives.usersSearch(activeScenarioId),
                            {
                                q: q.trim(),
                                exclude_assigned: excludeAssigned ? '1' : '0',
                                ...(roleId ? {role_id: roleId} : {}),
                            },
                        ),
                },
            },

            corporate: {
                flags: {
                    list: async <T = unknown>(params?: {
                        q?: string;
                        team?: string;
                        type?: string;
                        ordering?: string;
                    }) => {
                        const data = await v2Get<unknown>(apiRoutes.dashboard.scenarios.corporate.flags, params);
                        // This endpoint sometimes returns an array (no pagination)
                        return normalizePaginated<T>(data);
                    },

                    create: async <T = unknown>(payload: {
                        scenario_id: string;
                        team: string;
                        question: string;
                        answer: string;
                        hint: string;
                        hint_penalty?: number | null;
                        score?: number;
                        category_id?: string;
                        type?: string
                    }) =>
                        v2Post<T>(apiRoutes.dashboard.scenarios.corporate.flagCreate, payload),
                    update: async <T = unknown>(flagId: string, payload: unknown) =>
                        v2Patch<T>(apiRoutes.dashboard.scenarios.corporate.flagUpdate(flagId), payload),
                    delete: async (flagId: string) => {
                        await axiosClientAPI.delete(apiRoutes.dashboard.scenarios.corporate.flagUpdate(flagId));
                    },
                },
            },
        },

        injectTypes: {
            list: async () => v2Get<InjectType[]>(apiRoutes.dashboard.injectTypes),
        },
        roles: {
            list: async () => v2Get<InjectRole[]>(apiRoutes.dashboard.roles.list),
            create: async (payload: { name: string; code?: string }) =>
                v2Post<InjectRole>(apiRoutes.dashboard.roles.list, payload),
            update: async (id: number, payload: Partial<{ name: string; code?: string; is_active?: boolean }>) =>
                v2Patch<InjectRole>(apiRoutes.dashboard.roles.detail(id), payload),
        },

        companies: {
            list: async () => v2Get<Company[]>(apiRoutes.dashboard.companies.list),
        },

        phases: {
            list: async (companyFilter?: number | 'none') => {
                const url = companyFilter !== undefined
                    ? `${apiRoutes.dashboard.phases.list}?company=${companyFilter}`
                    : apiRoutes.dashboard.phases.list;
                return v2Get<Phase[]>(url);
            },
            get: async (id: number) => v2Get<Phase>(apiRoutes.dashboard.phases.detail(id)),
            create: async (payload: PhasePayload) =>
                v2Post<Phase>(apiRoutes.dashboard.phases.list, payload),
            update: async (id: number, payload: Partial<PhasePayload>) =>
                v2Patch<Phase>(apiRoutes.dashboard.phases.detail(id), payload),
            delete: async (id: number) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.phases.detail(id));
            },
            exercisePhases: {
                listByExerciseId: async (exerciseId: string) =>
                    v2Get<ExercisePhase[]>(apiRoutes.dashboard.phases.exercisePhases.listByExerciseId(exerciseId)),
                create: async (exerciseId: string, payload: ExercisePhasePayload) =>
                    v2Post<ExercisePhase>(
                        apiRoutes.dashboard.phases.exercisePhases.listByExerciseId(exerciseId),
                        payload,
                    ),
                get: async (exerciseId: string, id: number) =>
                    v2Get<ExercisePhase>(apiRoutes.dashboard.phases.exercisePhases.detail(exerciseId, id)),
                update: async (exerciseId: string, id: number, payload: Partial<ExercisePhasePayload>) =>
                    v2Patch<ExercisePhase>(
                        apiRoutes.dashboard.phases.exercisePhases.detail(exerciseId, id),
                        payload,
                    ),
                delete: async (exerciseId: string, id: number) => {
                    await axiosClientAPI.delete(
                        apiRoutes.dashboard.phases.exercisePhases.detail(exerciseId, id),
                    );
                },
            },
        },
        walkthroughs: {
            listByExerciseId: async (exerciseId: string) =>
                v2Get<ExerciseWalkthrough[]>(
                    apiRoutes.dashboard.walkthroughs.listByExerciseId(exerciseId),
                ),
            create: async (exerciseId: string, payload: ExerciseWalkthroughPayload) => {
                const formData = new FormData();
                if (payload.title !== undefined) formData.append('title', payload.title);
                if (payload.exercise_team !== undefined) formData.append('exercise_team', String(payload.exercise_team));
                if (payload.file) formData.append('file', payload.file);
                if (payload.is_active !== undefined) formData.append('is_active', String(payload.is_active));
                return v2Post<ExerciseWalkthrough>(
                    apiRoutes.dashboard.walkthroughs.listByExerciseId(exerciseId),
                    formData,
                );
            },
            get: async (exerciseId: string, id: number) =>
                v2Get<ExerciseWalkthrough>(apiRoutes.dashboard.walkthroughs.detail(exerciseId, id)),
            update: async (exerciseId: string, id: number, payload: ExerciseWalkthroughPayload) => {
                const formData = new FormData();
                if (payload.title !== undefined) formData.append('title', payload.title);
                if (payload.exercise_team !== undefined) formData.append('exercise_team', String(payload.exercise_team));
                if (payload.file) formData.append('file', payload.file);
                if (payload.is_active !== undefined) formData.append('is_active', String(payload.is_active));
                const res = await axiosClientAPI.patch<ExerciseWalkthrough>(
                    apiRoutes.dashboard.walkthroughs.detail(exerciseId, id),
                    formData,
                );
                return res.data;
            },
            delete: async (exerciseId: string, id: number) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.walkthroughs.detail(exerciseId, id));
            },
        },
        exerciseTeamMappings: {
            listByExerciseId: async (exerciseId: string) =>
                v2Get<ExerciseTeamMapping[]>(
                    apiRoutes.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId),
                ),
            create: async (exerciseId: string, payload: ExerciseTeamMappingPayload) =>
                v2Post<ExerciseTeamMapping>(
                    apiRoutes.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId),
                    payload,
                ),
            update: async (exerciseId: string, id: number, payload: Partial<ExerciseTeamMappingPayload>) =>
                v2Patch<ExerciseTeamMapping>(
                    apiRoutes.dashboard.exerciseTeamMappings.detail(exerciseId, id),
                    payload,
                ),
            delete: async (exerciseId: string, id: number) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.exerciseTeamMappings.detail(exerciseId, id));
            },
        },
        exerciseFlags: {
            listByExerciseId: async (exerciseId: string) =>
                v2Get<ExerciseFlagMapping[]>(
                    apiRoutes.dashboard.exerciseFlags.listByExerciseId(exerciseId),
                ),
            create: async (exerciseId: string, payload: ExerciseFlagPayload) =>
                v2Post<ExerciseFlagMapping>(
                    apiRoutes.dashboard.exerciseFlags.listByExerciseId(exerciseId),
                    payload,
                ),
            update: async (
                exerciseId: string,
                id: number,
                payload: Partial<ExerciseFlagPayload>,
            ) =>
                v2Patch<ExerciseFlagMapping>(
                    apiRoutes.dashboard.exerciseFlags.detail(exerciseId, id),
                    payload,
                ),
            delete: async (exerciseId: string, id: number) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.exerciseFlags.detail(exerciseId, id));
            },
        },
        flags: {
            list: async (params?: { team_id?: number; phase_id?: number }) =>
                v2Get<FlagModelDto[]>(apiRoutes.dashboard.flags.list, {
                    team_id: params?.team_id,
                    phase_id: params?.phase_id,
                }),
            create: async (payload: FlagModelPayload) => v2Post<FlagModelDto>(apiRoutes.dashboard.flags.list, payload),
            get: async (id: string) => v2Get<FlagModelDto>(apiRoutes.dashboard.flags.detail(id)),
            update: async (id: string, payload: Partial<FlagModelPayload>) =>
                v2Patch<FlagModelDto>(apiRoutes.dashboard.flags.detail(id), payload),
            delete: async (id: string) => {
                await axiosClientAPI.delete(apiRoutes.dashboard.flags.detail(id));
            },
        },
        /**
         * Relational exercise run history: immutable archives (no live ExerciseModel joins).
         */
        exerciseArchives: {
            listByExerciseId: async (exerciseId: string) =>
                v2Get<ExerciseArchiveListResponse>(
                    apiRoutes.dashboard.exerciseArchives.listByExerciseId(exerciseId),
                ),
            listAll: async (params?: {
                page?: number;
                page_size?: number;
                exercise_id?: string;
                date_filter?: 'today' | 'yesterday';
                archived_from?: string;
                archived_to?: string;
            }) => v2Get<ExerciseArchiveListResponse>(apiRoutes.dashboard.exerciseArchives.listAll, params),
            bulkReports: async (archiveUuids: string[]) =>
                v2Post<ExerciseArchiveBulkReportResponse>(
                    apiRoutes.dashboard.exerciseArchives.bulkReports,
                    {archive_uuids: archiveUuids},
                ),
            immutableReport: async (archiveUuid: string) =>
                v2Get<ImmutableExerciseReport>(
                    apiRoutes.dashboard.exerciseArchives.immutableReport(archiveUuid),
                ),
        },
        /**
         * GET relational exercise run by active id (run PK): /api/scenarios/active/:id
         */
        activeRunDetail: async (activeId: string) =>
            v2Get<ExerciseRunResponse>(apiRoutes.dashboard.activeRunDetail(activeId)),
        /**
         * Django relational runs: list + start (launch) — no Mongo active-scenario resolution.
         */
        exerciseRuns: {
            listByExerciseId: async (exerciseId: string) =>
                v2Get<ExerciseRunResponse[]>(apiRoutes.dashboard.exerciseRuns.listByExerciseId(exerciseId)),
            create: async (
                exerciseId: string,
                payload?: { name?: string; scenario_id?: string; status?: string },
            ) => v2Post<ExerciseRunResponse>(apiRoutes.dashboard.exerciseRuns.create(exerciseId), payload ?? {}),
            get: async (exerciseId: string, runId: number) =>
                v2Get<ExerciseRunResponse>(apiRoutes.dashboard.exerciseRuns.detail(exerciseId, runId)),
            start: async (exerciseId: string, runId: number) =>
                v2Post<ExerciseRunResponse>(apiRoutes.dashboard.exerciseRuns.start(exerciseId, runId)),
            startScenario: async (runId: number) =>
                v2Post<ExerciseRunResponse>(apiRoutes.dashboard.exerciseRuns.startScenario(runId), {}),
            finalizeParticipants: async (runId: number) =>
                v2Post<ExerciseRunResponse>(apiRoutes.dashboard.exerciseRuns.finalizeParticipants(runId), {}),
            participants: {
                list: async (exerciseId: string, runId: number) =>
                    v2Get<ExerciseRunParticipant[]>(apiRoutes.dashboard.exerciseRuns.participants(exerciseId, runId)),
                add: async (
                    exerciseId: string,
                    runId: number,
                    payload: { user_id: number; role_id: number; access_mode?: ParticipantAccessMode },
                ) =>
                    v2Post<ExerciseRunParticipant>(
                        apiRoutes.dashboard.exerciseRuns.participants(exerciseId, runId),
                        payload,
                    ),
                remove: async (exerciseId: string, runId: number, userId: string) => {
                    await axiosClientAPI.delete(
                        apiRoutes.dashboard.exerciseRuns.participantRemove(exerciseId, runId, userId),
                    );
                },
                update: async (
                    exerciseId: string,
                    runId: number,
                    userId: string,
                    payload: {
                        access_mode?: ParticipantAccessMode;
                        compute_instance_id?: string | null;
                    },
                ) =>
                    v2Patch<ExerciseRunParticipant>(
                        apiRoutes.dashboard.exerciseRuns.participantRemove(exerciseId, runId, userId),
                        payload,
                    ),
                searchUsers: async (
                    exerciseId: string,
                    runId: number,
                    q: string,
                    excludeAssigned = true,
                    roleId?: string,
                ) =>
                    v2Get<ExerciseRunUserSearchItem[]>(
                        apiRoutes.dashboard.exerciseRuns.usersSearch(exerciseId, runId),
                        {
                            q: q.trim(),
                            exclude_assigned: excludeAssigned ? '1' : '0',
                            ...(roleId ? {role_id: roleId} : {}),
                        },
                    ),
            },
            participantRoleOptions: async (
                exerciseId: string,
                runId: number,
                accessMode: ParticipantAccessMode,
            ) =>
                v2Get<ParticipantRoleOptionsResponse>(
                    apiRoutes.dashboard.exerciseRuns.participantRoleOptions(exerciseId, runId),
                    {access_mode: accessMode},
                ),
        },
    },

    /**
     * Non-dashboard v2 helpers (aliases).
     */
    users: {
        detail: async <T = unknown>(userId: string) => v2Get<T>(apiRoutes.users.detail(userId)),
        /**
         * Corporate scenario reports list for a given user.
         * Backend: GET /api/v2/users/<user_id>/report
         */
        report: async (userId: string) => v2Get<TReportList>(apiRoutes.users.reports(userId)),
        changePassword: async <T = unknown>(
            userId: string,
            payload: { email: string; password: string; confirm_password: string },
        ) => v2Post<T>(apiRoutes.users.changePassword(userId), payload),
        update: async <T = unknown>(userId: string, payload: unknown) =>
            v2Patch<T>(apiRoutes.users.update(userId), payload),
    },

    reports: {
        /**
         * Corporate report detail by report_id.
         * Backend: GET /api/v2/reports/<report_id>
         */
        detail: async (reportId: string) => v2Get<TReportDetailItem>(apiRoutes.reports.detail(reportId)),
    },

    avatar: {
        list: async <T = unknown>() => v2Get<T>(apiRoutes.avatar.list),
    },
} as const;


